// @ts-check

/**
 * @file Shared user-role route options, translations, and URL helpers.
 */

import {optionalRouteParam} from "../../shared/route-params"

export const ROLE_OPTIONS = [
  {value: "administrator"},
  {value: "organization_administrator"}
]

const CARRIED_SEARCH_PARAMS = [
  "backendHost",
  "backendPort",
  "backendProtocol",
  "platform",
  "systemTest",
  "systemTestClientWsPort",
  "systemTestScoundrelPort"
]

export const TRANSLATIONS = {
  da: {
    addNewUserRole: "Tilføj ny brugerrolle",
    administrator: "Administrator",
    cancelDelete: "Annuller",
    chooseRole: "Vælg en rolle",
    confirmDelete: "Slet brugerrolle",
    delete: "Slet",
    deleted: "Brugerrollen blev slettet",
    edit: "Rediger",
    editUserRole: "Rediger brugerrolle",
    empty: "Brugeren har ingen roller",
    forbidden: "Du har ikke adgang til denne side",
    general: "Generelt",
    loadErrorPrefix: "Kunne ikke indlæse brugerroller",
    loading: "Indlæser brugerroller...",
    loadingEditor: "Indlæser brugerrolle...",
    noUsers: "Ingen brugere er tilgængelige",
    organizationAdministrator: "Organisationsadministrator",
    role: "Rolle",
    rolesForUser: "Roller for %{name}",
    save: "Gem",
    saved: "Brugerrollen blev gemt",
    saving: "Gemmer...",
    selectUser: "Bruger",
    theUserAlreadyHasThatRole: "Brugeren har allerede den rolle",
    userRoles: "Brugerroller"
  },
  de: {
    addNewUserRole: "Neue Benutzerrolle hinzufügen",
    administrator: "Administrator",
    cancelDelete: "Abbrechen",
    chooseRole: "Rolle auswählen",
    confirmDelete: "Benutzerrolle löschen",
    delete: "Löschen",
    deleted: "Die Benutzerrolle wurde gelöscht",
    edit: "Bearbeiten",
    editUserRole: "Benutzerrolle bearbeiten",
    empty: "Der Benutzer hat keine Rollen",
    forbidden: "Sie haben keinen Zugriff auf diese Seite",
    general: "Allgemein",
    loadErrorPrefix: "Benutzerrollen konnten nicht geladen werden",
    loading: "Benutzerrollen werden geladen...",
    loadingEditor: "Benutzerrolle wird geladen...",
    noUsers: "Keine Benutzer verfügbar",
    organizationAdministrator: "Organisationsadministrator",
    role: "Rolle",
    rolesForUser: "Rollen für %{name}",
    save: "Speichern",
    saved: "Die Benutzerrolle wurde gespeichert",
    saving: "Speichert...",
    selectUser: "Benutzer",
    theUserAlreadyHasThatRole: "Der Benutzer hat diese Rolle bereits",
    userRoles: "Benutzerrollen"
  },
  en: {
    addNewUserRole: "Add new user role",
    administrator: "Administrator",
    cancelDelete: "Cancel",
    chooseRole: "Choose a role",
    confirmDelete: "Delete user role",
    delete: "Delete",
    deleted: "The user role was deleted",
    edit: "Edit",
    editUserRole: "Edit user role",
    empty: "The user has no roles",
    forbidden: "You do not have access to this page",
    general: "General",
    loadErrorPrefix: "Could not load user roles",
    loading: "Loading user roles...",
    loadingEditor: "Loading user role...",
    noUsers: "No users are available",
    organizationAdministrator: "Organization administrator",
    role: "Role",
    rolesForUser: "Roles for %{name}",
    save: "Save",
    saved: "The user role was saved",
    saving: "Saving...",
    selectUser: "User",
    theUserAlreadyHasThatRole: "The user already has that role",
    userRoles: "User roles"
  }
}

/**
 * Resolves the browser locale into a supported translation locale.
 * @returns {"da" | "de" | "en"} Supported locale code.
 */
export function currentLocale() {
  if (typeof navigator === "undefined" || typeof navigator.language !== "string") {
    return "en"
  }

  const locale = navigator.language.slice(0, 2).toLowerCase()

  if (locale === "da" || locale === "de") {
    return locale
  }

  return "en"
}

/**
 * Replaces named placeholders in a translated string.
 * @param {string} template Translation template containing `%{name}` placeholders.
 * @param {Record<string, string>} variables Placeholder values keyed by name.
 * @returns {string} Interpolated translation text.
 */
export function interpolate(template, variables) {
  return Object.entries(variables).reduce((result, [key, value]) => result.replace(`%{${key}}`, value), template)
}

/**
 * Builds a route path while carrying system-test query parameters.
 * @param {Record<string, string | string[] | undefined>} params Current route params.
 * @param {string} path Destination path to build.
 * @returns {string} Destination path with carried query params.
 */
export function routePathWithParams(params, path) {
  const url = new URL(path, "http://placeholder")

  for (const key of CARRIED_SEARCH_PARAMS) {
    const value = optionalRouteParam(params, key, key)

    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  }

  const search = url.searchParams.toString()

  if (search) {
    return `${url.pathname}?${search}`
  }

  return url.pathname
}

/**
 * Resolves a redirect target with optional external base URL support.
 * @param {Record<string, string | string[] | undefined>} params Current route params.
 * @param {string} redirectPath Redirect path or absolute URL.
 * @returns {string} Absolute or app-relative navigation target.
 */
export function resolveRedirectUrl(params, redirectPath) {
  if (redirectPath.startsWith("http://") || redirectPath.startsWith("https://")) {
    return redirectPath
  }

  const redirectPathWithParams = routePathWithParams(params, redirectPath)
  const redirectBaseUrl = (optionalRouteParam(params, "redirectBaseUrl", "Redirect base URL") ?? "").replace(/\/$/, "")

  if (redirectBaseUrl) {
    return `${redirectBaseUrl}${redirectPathWithParams}`
  }

  return redirectPathWithParams
}

/**
 * Translates a backend role identifier for display.
 * @param {"da" | "de" | "en"} locale Locale to translate with.
 * @param {string} role Backend role identifier.
 * @returns {string} Localized role label.
 */
export function translateRole(locale, role) {
  const translation = TRANSLATIONS[locale]

  if (role === "organization_administrator") {
    return translation.organizationAdministrator
  }

  return translation.administrator
}
